import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserCircle, Plus, X, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const RequesterSearch = ({
    value,
    onChange,
    requesters: externalRequesters,
    placeholder = 'Search requester...',
    allowCreate = true,
    className = '',
    inputClassName = '',
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState('');
    const [allRequesters, setAllRequesters] = useState(externalRequesters || []);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', email: '' });
    const [creating, setCreating] = useState(false);

    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const modalOpenRef = useRef(false);

    const setModal = (val) => {
        modalOpenRef.current = val;
        setModalOpen(val);
    };

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });

    // Load requesters on mount if not passed in
    useEffect(() => {
        if (!externalRequesters || externalRequesters.length === 0) {
            axios.get(`${API}/requesters`, getAuthHeaders())
                .then(r => setAllRequesters(r.data))
                .catch(() => { });
        }
    }, []); // eslint-disable-line

    // Sync when parent pushes updated list
    useEffect(() => {
        if (externalRequesters) setAllRequesters(externalRequesters);
    }, [externalRequesters]);

    // Sync display label with selected value
    useEffect(() => {
        if (!value) { setSelectedLabel(''); setQuery(''); return; }
        const found = allRequesters.find(r => r.id === value);
        if (found) { setSelectedLabel(found.name); setQuery(found.name); }
    }, [value, allRequesters]);

    // Filter results as user types
    useEffect(() => {
        if (!query.trim()) { setResults([]); return; }
        const q = query.toLowerCase();
        setResults(
            allRequesters
                .filter(r => r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q))
                .slice(0, 10)
        );
    }, [query, allRequesters]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (modalOpenRef.current) return;
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                if (value) {
                    const found = allRequesters.find(r => r.id === value);
                    setQuery(found ? found.name : '');
                } else {
                    setQuery('');
                }
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [value, allRequesters]);

    const handleSelect = (requester) => {
        setSelectedLabel(requester.name);
        setQuery(requester.name);
        setOpen(false);
        onChange(requester.id, requester);
    };

    const handleClear = () => {
        setQuery(''); setSelectedLabel(''); setOpen(false);
        onChange(null, null);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const openCreateModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setCreateForm({ name: query.trim(), email: '' });
        setOpen(false);
        setModal(true);
    };

    const handleCreateRequester = async (e) => {
        e.preventDefault();
        if (!createForm.name.trim()) return;
        setCreating(true);
        try {
            const res = await axios.post(`${API}/requesters`, createForm, getAuthHeaders());
            const newRequester = res.data;
            setAllRequesters(prev => [...prev, newRequester]);
            toast.success(`Requester "${newRequester.name}" created`);
            setModal(false);
            setCreateForm({ name: '', email: '' });
            handleSelect(newRequester);
        } catch {
            toast.error('Failed to create requester');
        } finally {
            setCreating(false);
        }
    };

    const noResults = !!query.trim() && results.length === 0;
    const showDropdown = open && (results.length > 0 || noResults);

    return (
        <>
            <div ref={containerRef} className={`relative ${className}`}>
                {/* Search input */}
                <div className="relative">
                    <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                        className={`pl-9 pr-8 bg-white ${inputClassName}`}
                        autoComplete="off"
                    />
                    {value && selectedLabel ? (
                        <button type="button" onClick={handleClear}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                            <X className="w-4 h-4" />
                        </button>
                    ) : (
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    )}
                </div>

                {/* Dropdown results */}
                {showDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                        {results.length > 0 && (
                            <ul className="max-h-60 overflow-y-auto divide-y divide-gray-50">
                                {results.map(r => (
                                    <li
                                        key={r.id}
                                        className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-blue-50 transition-colors ${value === r.id ? 'bg-blue-50' : ''}`}
                                        onMouseDown={(e) => { e.preventDefault(); handleSelect(r); }}
                                    >
                                        <UserCircle className="w-4 h-4 text-gray-400 shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-800 truncate">{r.name}</p>
                                            {r.email && <p className="text-xs text-gray-400 truncate">{r.email}</p>}
                                        </div>
                                        {value === r.id && (
                                            <span className="ml-auto text-xs text-blue-600 font-semibold shrink-0">Selected</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {noResults && (
                            <div className="px-3 py-2.5 text-sm text-gray-500 border-b border-gray-100">
                                No match for <strong>"{query}"</strong>
                            </div>
                        )}

                        {noResults && allowCreate && (
                            <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-3 hover:bg-emerald-50 text-emerald-700 font-medium text-sm transition-colors"
                                onMouseDown={openCreateModal}
                            >
                                <Plus className="w-4 h-4 shrink-0" />
                                <span>Add requester <strong>"{query}"</strong></span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ── Create Requester Modal ── */}
            <Dialog open={modalOpen} onOpenChange={(o) => { if (!creating) setModal(o); }}>
                <DialogContent className="sm:max-w-sm" onPointerDownOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserCircle className="w-4 h-4 text-emerald-600" />
                            Add Requester
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateRequester} className="space-y-4 pt-1">
                        <div className="space-y-2">
                            <Label htmlFor="req-name">
                                Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="req-name"
                                placeholder="Jane Smith"
                                value={createForm.name}
                                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                required
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="req-email">
                                Email{' '}
                                <span className="text-gray-400 font-normal">(optional)</span>
                            </Label>
                            <Input
                                id="req-email"
                                type="email"
                                placeholder="jane@company.com"
                                value={createForm.email}
                                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                            />
                        </div>
                        <div className="flex gap-2 pt-1">
                            <Button
                                type="submit"
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                disabled={creating}
                            >
                                {creating
                                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating…</>
                                    : 'Create & Select'
                                }
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setModal(false)}
                                disabled={creating}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RequesterSearch;
